import { IconCheck } from '@tabler/icons-react';
import type { ComponentType, ReactNode } from 'react';

import { DEFAULT_COLOR_SHADE, type MantineColor, mantineColorVar } from '#core/lib/formatters/colors.ts';

import { SEMANTIC_TOKENS, type ToolSemantic } from './semantic-tokens.ts';
import type { ToolDisplaySpec } from '#plugin-chat/lib/tool/message-formatters.ts';

/**
 * Fallback color for the complete slot when neither `complete.semantic` nor
 * `complete.color` is set. `gray` is intentionally outside
 * {@link MantineColor} (which restricts to the project palette), so it stays
 * inline rather than going through {@link mantineColorVar}.
 */
const FALLBACK_COMPLETE_COLOR = 'var(--mantine-color-gray-6)';

interface ResolvedCompleteToken {
  color: string;
  icon: ComponentType<{ size?: number | string; color?: string }>;
}

/**
 * Resolve the complete-slot color + icon for a {@link ToolDisplaySpec}.
 *
 * Resolution rules:
 * - `semantic` looks up the coordinated color/shade/icon from
 *   {@link SEMANTIC_TOKENS}.
 * - Explicit `color` / `shade` / `icon` override the semantic lookup
 *   field-by-field, so callers can keep the semantic icon while overriding
 *   the color (or vice versa).
 * - When no color or semantic is set, falls back to a neutral gray; when no
 *   icon is set, falls back to {@link IconCheck}.
 */
export function resolveCompleteToken(spec: {
  semantic?: ToolSemantic;
  color?: MantineColor;
  shade?: number;
  icon?: ComponentType<{ size?: number | string; color?: string }>;
}): ResolvedCompleteToken {
  const semanticToken = spec.semantic ? SEMANTIC_TOKENS[spec.semantic] : undefined;
  const color = spec.color ?? semanticToken?.color;
  const shade = spec.shade ?? semanticToken?.shade ?? DEFAULT_COLOR_SHADE;
  const icon = spec.icon ?? semanticToken?.icon ?? IconCheck;
  return {
    color: color ? mantineColorVar(color, shade) : FALLBACK_COMPLETE_COLOR,
    icon,
  };
}

/**
 * Resolve a slot field that can be either a static `ReactNode` or a function
 * of `args` (and optionally `result`). Returns `undefined` when the field
 * is unset.
 */
export function resolveSlot<ArgsType, ResultType, Extra extends object = object>(
  slot: ReactNode | ((input: { args: ArgsType; result?: ResultType } & Extra) => ReactNode) | undefined,
  input: { args: ArgsType; result?: ResultType } & Extra,
): ReactNode {
  if (typeof slot === 'function') {
    return (slot as (input: { args: ArgsType; result?: ResultType } & Extra) => ReactNode)(input);
  }
  return slot;
}

/**
 * Narrowed type for `display.complete` when authored as the declarative
 * object form. Mirrors {@link ToolDisplaySpec}'s inline complete type.
 */
export type DeclarativeComplete<ArgsType extends Record<string, unknown>, ResultType> = Exclude<
  NonNullable<ToolDisplaySpec<ArgsType, ResultType>['complete']>,
  ReactNode
>;

/**
 * Predicate: is `complete` the declarative object form (with `label`) rather
 * than a plain `ReactNode`?
 *
 * `ReactNode` includes objects with no `label` field, so checking `'label'
 * in value` discriminates the two shapes — `ReactElement` carries `type` /
 * `props` / `key` but not `label`.
 */
export function isDeclarativeComplete<ArgsType extends Record<string, unknown>, ResultType>(
  value: ToolDisplaySpec<ArgsType, ResultType>['complete'],
): value is DeclarativeComplete<ArgsType, ResultType> {
  return typeof value === 'object' && value !== null && 'label' in value;
}
